/**
 * CLIP Studio Paint (.clip) ファイルからサムネイル画像を抽出するユーティリティ。
 *
 * .clip ファイルは SQLite データベース形式です。CanvasPreview テーブルの
 * ImageData カラムからプレビュー PNG/JPEG を取得し、リサイズして返します。
 * .clip 本体はサーバーにアップロードしません。
 */

// SQLite ファイル先頭マジックバイト "SQLite format 3\0"
const SQLITE_MAGIC = new Uint8Array([
  0x53, 0x51, 0x4c, 0x69, 0x74, 0x65, 0x20, 0x66,
  0x6f, 0x72, 0x6d, 0x61, 0x74, 0x20, 0x33, 0x00,
])

/** サムネイル生成を試みる .clip ファイルの最大サイズ (200 MB) */
export const MAX_CLIP_FILE_SIZE = 200 * 1024 * 1024

/** 生成するサムネイルの最大辺長 (px) */
const THUMB_MAX_DIM = 1920

// ─── エラー型 ─────────────────────────────────────────────────────

export class ClipExtractionError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ClipExtractionError'
  }
}

export class ClipSizeError extends ClipExtractionError {
  constructor() {
    super(
      '.clipファイルのサイズが大きすぎるため、サムネイルを自動生成できません。' +
      'PNG/JPEGに書き出して添付してください。',
    )
    this.name = 'ClipSizeError'
  }
}

// ─── SQLite 低レベルユーティリティ ───────────────────────────────

interface SqliteCtx {
  buf: Uint8Array
  /** SQLite マジックバイトのバッファ内オフセット */
  base: number
  pageSize: number
  /** 使用可能ページサイズ = pageSize - reserved (ページ末尾の予約領域) */
  usableSize: number
}

function findSqliteOffset(buf: Uint8Array): number {
  outer: for (let i = 0; i <= buf.length - 16; i++) {
    for (let j = 0; j < 16; j++) {
      if (buf[i + j] !== SQLITE_MAGIC[j]) continue outer
    }
    return i
  }
  return -1
}

function u16(b: Uint8Array, o: number): number {
  return (b[o] << 8) | b[o + 1]
}

function u32(b: Uint8Array, o: number): number {
  return ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0
}

/**
 * SQLite 可変長整数 (varint) を読む。
 * 戻り値: [値, 読んだバイト数]
 */
function readVarint(b: Uint8Array, o: number): [number, number] {
  let v = 0
  for (let i = 0; i < 9; i++) {
    const byte = b[o + i]
    if (i < 8) {
      v = v * 128 + (byte & 0x7f)
      if (!(byte & 0x80)) return [v, i + 1]
    } else {
      // 9 バイト目は全 8 ビット使用
      v = v * 256 + byte
      return [v, 9]
    }
  }
  return [v, 9]
}

/** SQLite シリアル型のバイト長を返す */
function serialTypeBytes(st: number): number {
  if (st === 0) return 0         // NULL
  if (st <= 4) return st         // 1〜4 バイト整数
  if (st === 5) return 6         // 6 バイト整数
  if (st <= 7) return 8          // 8 バイト整数または float
  if (st <= 9) return 0          // 定数 0 / 1（バイトなし）
  if (st >= 12) return st % 2 === 0 ? (st - 12) / 2 : (st - 13) / 2
  return 0
}

/** 1 ベースのページ番号から Uint8Array (subarray) を返す */
function getPage(ctx: SqliteCtx, pageNum: number): Uint8Array {
  const start = ctx.base + (pageNum - 1) * ctx.pageSize
  return ctx.buf.subarray(start, start + ctx.pageSize)
}

/**
 * テーブル B-tree リーフセルのペイロードを読む。
 * オーバーフローページも追跡して完全なペイロードを返す。
 *
 * ★ overflow 判定に使う X の式は「テーブルリーフ専用」の U-35 を使用。
 *    インデックスページ用の (U-12)*64/255-23 と混同しないこと。
 *    （SQLite btree.c: maxLeaf = usableSize - 35）
 */
function readLeafPayload(
  ctx: SqliteCtx,
  page: Uint8Array,
  cellOff: number,
): { payload: Uint8Array; rowId: number } {
  let off = cellOff
  const [payloadSize, ps] = readVarint(page, off); off += ps
  const [rowId, rs] = readVarint(page, off); off += rs

  const U = ctx.usableSize
  // テーブル B-tree リーフ専用の最大ローカルサイズ (SQLite: maxLeaf = usableSize - 35)
  const X = U - 35
  // 最小ローカルサイズ (SQLite: minLeaf = (usableSize-12)*32/255 - 23)
  const M = Math.floor((U - 12) * 32 / 255) - 23

  let localSize: number
  if (payloadSize <= X) {
    // overflow なし: ペイロード全体をローカルに格納
    localSize = payloadSize
  } else {
    // overflow あり: K = M + (P-M)%(U-4)
    const K = M + (payloadSize - M) % (U - 4)
    localSize = K <= X ? K : M
  }

  const payload = new Uint8Array(payloadSize)
  payload.set(page.subarray(off, off + localSize))

  if (payloadSize > localSize) {
    // overflow ポインタは localSize バイト直後の 4 バイト
    let overflowPage = u32(page, off + localSize)
    let written = localSize
    while (overflowPage !== 0 && written < payloadSize) {
      const op = getPage(ctx, overflowPage)
      const nextOvfl = u32(op, 0)
      const chunk = Math.min(U - 4, payloadSize - written)
      payload.set(op.subarray(4, 4 + chunk), written)
      written += chunk
      overflowPage = nextOvfl
    }
  }

  return { payload, rowId }
}

// ─── sqlite_master からテーブルの rootpage を探す ────────────────

function findTableRootPage(ctx: SqliteCtx, tableName: string): number | null {
  return scanSchemaPage(ctx, 1, tableName)
}

function scanSchemaPage(ctx: SqliteCtx, pageNum: number, tableName: string): number | null {
  const page = getPage(ctx, pageNum)
  // ページ 1 だけ先頭 100 バイトがデータベースヘッダー
  const hdrOff = pageNum === 1 ? 100 : 0
  const pageType = page[hdrOff]
  const numCells = u16(page, hdrOff + 3)
  const cellPtrBase = hdrOff + (pageType === 0x05 ? 12 : 8)

  if (pageType === 0x0d) {
    for (let i = 0; i < numCells; i++) {
      const cellPtr = u16(page, cellPtrBase + i * 2)
      const rootPage = parseSchemaCell(ctx, page, cellPtr, tableName)
      if (rootPage !== null) return rootPage
    }
    return null
  }

  if (pageType === 0x05) {
    for (let i = 0; i < numCells; i++) {
      const cellPtr = u16(page, cellPtrBase + i * 2)
      const childPage = u32(page, cellPtr)
      const result = scanSchemaPage(ctx, childPage, tableName)
      if (result !== null) return result
    }
    const rightmost = u32(page, hdrOff + 8)
    return scanSchemaPage(ctx, rightmost, tableName)
  }

  return null
}

/**
 * sqlite_master のリーフセルを解析し、対象テーブルの rootpage を返す。
 * sqlite_master スキーマ: type(0), name(1), tbl_name(2), rootpage(3), sql(4)
 */
function parseSchemaCell(
  ctx: SqliteCtx,
  page: Uint8Array,
  cellOff: number,
  targetTable: string,
): number | null {
  const { payload } = readLeafPayload(ctx, page, cellOff)

  let pOff = 0
  const [headerLen, hls] = readVarint(payload, pOff); pOff += hls

  const serialTypes: number[] = []
  while (pOff < headerLen) {
    const [st, sts] = readVarint(payload, pOff); pOff += sts
    serialTypes.push(st)
  }

  let valOff = headerLen

  // col 0: type (text) — 'table' でなければスキップ
  const st0 = serialTypes[0] ?? 0
  if (st0 >= 13 && st0 % 2 !== 0) {
    const len = (st0 - 13) / 2
    const typeStr = new TextDecoder().decode(payload.subarray(valOff, valOff + len))
    valOff += len
    if (typeStr !== 'table') return null
  } else {
    valOff += serialTypeBytes(st0)
    return null
  }

  // col 1: name (text) — 対象テーブル名か確認
  const st1 = serialTypes[1] ?? 0
  if (st1 >= 13 && st1 % 2 !== 0) {
    const len = (st1 - 13) / 2
    const nameStr = new TextDecoder().decode(payload.subarray(valOff, valOff + len))
    valOff += len
    if (nameStr.toLowerCase() !== targetTable.toLowerCase()) return null
  } else {
    valOff += serialTypeBytes(st1)
    return null
  }

  // col 2: tbl_name (skip)
  valOff += serialTypeBytes(serialTypes[2] ?? 0)

  // col 3: rootpage (integer)
  const st3 = serialTypes[3] ?? 0
  if (st3 === 0 || st3 === 8) return null
  if (st3 === 9) return 1
  if (st3 === 1) return payload[valOff]
  if (st3 === 2) return u16(payload, valOff)
  if (st3 === 3) return (payload[valOff] << 16) | (payload[valOff + 1] << 8) | payload[valOff + 2]
  if (st3 === 4) return u32(payload, valOff)
  return null
}

// ─── CanvasPreview テーブルから ImageData BLOB を取得 ─────────────

/**
 * CanvasPreview テーブルの最初のレコードから ImageData BLOB を取得する。
 * スキーマ: ID INTEGER PRIMARY KEY, ImageData BLOB
 * → ID は rowid エイリアス (NULL / serial type 0)、ImageData が最初の BLOB カラム。
 */
function findImageDataBlob(ctx: SqliteCtx, rootPage: number): Uint8Array | null {
  return scanTablePage(ctx, rootPage)
}

function scanTablePage(ctx: SqliteCtx, pageNum: number): Uint8Array | null {
  const page = getPage(ctx, pageNum)
  const pageType = page[0]
  const numCells = u16(page, 3)
  const cellPtrBase = pageType === 0x05 ? 12 : 8

  if (pageType === 0x0d) {
    for (let i = 0; i < numCells; i++) {
      const cellPtr = u16(page, cellPtrBase + i * 2)
      const blob = parseImageDataCell(ctx, page, cellPtr)
      if (blob !== null) return blob
    }
    return null
  }

  if (pageType === 0x05) {
    for (let i = 0; i < numCells; i++) {
      const cellPtr = u16(page, cellPtrBase + i * 2)
      const childPage = u32(page, cellPtr)
      const result = scanTablePage(ctx, childPage)
      if (result !== null) return result
    }
    const rightmost = u32(page, 8)
    return scanTablePage(ctx, rightmost)
  }

  return null
}

function parseImageDataCell(
  ctx: SqliteCtx,
  page: Uint8Array,
  cellOff: number,
): Uint8Array | null {
  const { payload } = readLeafPayload(ctx, page, cellOff)

  let pOff = 0
  const [headerLen, hls] = readVarint(payload, pOff); pOff += hls

  const serialTypes: number[] = []
  while (pOff < headerLen) {
    const [st, sts] = readVarint(payload, pOff); pOff += sts
    serialTypes.push(st)
  }

  let valOff = headerLen
  for (let i = 0; i < serialTypes.length; i++) {
    const st = serialTypes[i]
    // BLOB: 偶数かつ >= 12
    if (st >= 12 && st % 2 === 0) {
      const blobLen = (st - 12) / 2
      if (blobLen > 0) {
        return payload.slice(valOff, valOff + blobLen)
      }
    }
    valOff += serialTypeBytes(st)
  }
  return null
}

// ─── 画像判定・リサイズ ───────────────────────────────────────────

function isValidImageBytes(data: Uint8Array): boolean {
  if (data.length < 8) return false
  // PNG: 89 50 4E 47 0D 0A 1A 0A
  if (data[0] === 0x89 && data[1] === 0x50 && data[2] === 0x4e && data[3] === 0x47) return true
  // JPEG: FF D8 FF
  if (data[0] === 0xff && data[1] === 0xd8 && data[2] === 0xff) return true
  return false
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = (e) => {
      console.error('[.clip] image.onerror:', e)
      reject(new ClipExtractionError('サムネイル画像の読み込みに失敗しました'))
    }
    img.src = url
  })
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality?: number,
): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality))
}

async function resizeToThumbnail(imageData: Uint8Array, baseName: string, clipFileName: string): Promise<File> {
  // ★ ユーザー要求: PNG を優先して変換 (WebP は後回し)
  const rawBlob = new Blob([new Uint8Array(imageData).buffer as ArrayBuffer])
  const url = URL.createObjectURL(rawBlob)

  try {
    const img = await loadImage(url)

    // naturalWidth が 0 なら画像デコード失敗
    if (img.naturalWidth === 0 || img.naturalHeight === 0) {
      console.error(`[.clip] 画像デコード失敗: naturalWidth=${img.naturalWidth}, naturalHeight=${img.naturalHeight}`)
      throw new ClipExtractionError('サムネイル画像のデコードに失敗しました（画像サイズが 0 です）')
    }

    console.log(`[.clip] ${clipFileName}: 元画像サイズ ${img.naturalWidth}x${img.naturalHeight}`)

    let w = img.naturalWidth
    let h = img.naturalHeight
    if (w > THUMB_MAX_DIM || h > THUMB_MAX_DIM) {
      const scale = THUMB_MAX_DIM / Math.max(w, h)
      w = Math.round(w * scale)
      h = Math.round(h * scale)
    }

    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx2d = canvas.getContext('2d')
    if (!ctx2d) throw new ClipExtractionError('Canvas コンテキストの取得に失敗しました')
    ctx2d.drawImage(img, 0, 0, w, h)

    // PNG に変換 (WebP は後回し)
    const pngBlob = await canvasToBlob(canvas, 'image/png')
    if (!pngBlob || pngBlob.size === 0) {
      throw new ClipExtractionError('サムネイル画像の PNG 変換に失敗しました')
    }

    const outFile = new File([pngBlob], `${baseName}_preview.png`, { type: 'image/png' })
    console.log(`[.clip] ${clipFileName}: 生成ファイル ${outFile.name} (${outFile.type}, ${outFile.size} bytes)`)
    return outFile
  } finally {
    URL.revokeObjectURL(url)
  }
}

// ─── 公開 API ────────────────────────────────────────────────────

/**
 * .clip ファイルからサムネイル画像を抽出し、PNG の File として返す。
 *
 * @throws {ClipSizeError} ファイルサイズが MAX_CLIP_FILE_SIZE を超える場合
 * @throws {ClipExtractionError} その他の抽出失敗
 */
export async function extractClipThumbnail(file: File): Promise<File> {
  if (file.size > MAX_CLIP_FILE_SIZE) throw new ClipSizeError()

  const arrayBuffer = await file.arrayBuffer()
  const buf = new Uint8Array(arrayBuffer)

  try {
    const base = findSqliteOffset(buf)
    if (base < 0) {
      throw new ClipExtractionError('SQLite データベースが見つかりませんでした')
    }

    let pageSize = u16(buf, base + 16)
    if (pageSize === 1) pageSize = 65536  // SQLite の特殊値: 65536

    // ページ末尾予約領域 (通常 0)
    const reservedPerPage = buf[base + 20]
    const usableSize = pageSize - reservedPerPage

    console.log(`[.clip] ${file.name}: ファイルサイズ=${file.size} bytes, pageSize=${pageSize}, reserved=${reservedPerPage}, usableSize=${usableSize}`)

    const ctx: SqliteCtx = { buf, base, pageSize, usableSize }

    const rootPage = findTableRootPage(ctx, 'CanvasPreview')
    if (rootPage === null || rootPage === 0) {
      throw new ClipExtractionError('CanvasPreview テーブルが見つかりませんでした')
    }
    console.log(`[.clip] ${file.name}: CanvasPreview rootPage=${rootPage}`)

    const imageData = findImageDataBlob(ctx, rootPage)
    if (!imageData || imageData.length === 0) {
      throw new ClipExtractionError('サムネイル画像データが見つかりませんでした')
    }

    // デバッグ: 抽出 BLOB の情報
    const first16 = Array.from(imageData.slice(0, 16))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join(' ')
    console.log(`[.clip] ${file.name}: BLOB サイズ=${imageData.length} bytes, 先頭16バイト: ${first16}`)

    if (!isValidImageBytes(imageData)) {
      console.error(`[.clip] ${file.name}: 有効な画像データではありません (PNG/JPEG magic なし)`)
      throw new ClipExtractionError(
        'サムネイルデータが有効な画像形式ではありません (PNG/JPEG magic bytes が一致しません)',
      )
    }

    const baseName = file.name.replace(/\.clip$/i, '')
    return await resizeToThumbnail(imageData, baseName, file.name)
  } catch (err) {
    if (err instanceof ClipExtractionError) throw err
    console.error('[.clip] 予期しないエラー:', err)
    throw new ClipExtractionError(
      'この.clipファイルからサムネイルを生成できませんでした。' +
      'CLIP STUDIO PAINTからPNG/JPEGに書き出して添付してください。',
    )
  }
}
